import { CRM_LEGACY_ALIAS_MODULES } from './accessModules.js';

export const normalizeRoleName = (value) => String(value || '').trim().toLowerCase();

export const toModuleIdList = (rows = []) =>
  (rows || [])
    .map((row) => row?.module_id)
    .filter(Boolean);

export function buildEffectiveModulePermissions({
  baseRows = [],
  overrideRows = [],
}) {
  const effective = new Set(toModuleIdList(baseRows));

  for (const row of overrideRows || []) {
    const moduleId = row?.module_id;
    if (!moduleId) continue;

    if (row?.is_allowed === false) {
      effective.delete(moduleId);
      continue;
    }

    effective.add(moduleId);
  }

  return applyLegacyModuleAliases(effective, collectDeniedModuleIds(overrideRows));
}

export const collectDeniedModuleIds = (overrideRows = []) =>
  new Set(
    (overrideRows || [])
      .filter((row) => row?.module_id && row?.is_allowed === false)
      .map((row) => row.module_id),
  );

/**
 * Expande los alias heredados del CRM (CRM_LEGACY_ALIAS_MODULES) sobre el set
 * efectivo: un modulo nuevo se otorga si el usuario tiene alguno de sus modulos
 * "padre" (p. ej. Banco de Horas via Proyectos), para no dejar sin acceso a
 * usuarios creados antes de que el modulo existiera.
 *
 * Un DENY explicito del usuario (role_permissions.is_allowed=false) le gana al
 * alias. Antes el alias se resolvia en el chequeo de acceso, donde ya no habia
 * forma de distinguir "sin fila" de "denegado", y el deny no tenia efecto.
 *
 * Un solo nivel, contra el set original (no transitivo), igual que el chequeo
 * historico de hasPermission.
 */
export function applyLegacyModuleAliases(effective, deniedModuleIds = new Set()) {
  const source = new Set(effective || []);
  const result = new Set(source);
  const denied = deniedModuleIds instanceof Set ? deniedModuleIds : new Set(deniedModuleIds || []);

  for (const [moduleId, aliasIds] of Object.entries(CRM_LEGACY_ALIAS_MODULES)) {
    if (result.has(moduleId) || denied.has(moduleId)) continue;
    if ((aliasIds || []).some((aliasId) => source.has(aliasId))) result.add(moduleId);
  }

  return result;
}

export function buildUserModuleOverrides({
  baseModuleIds = [],
  selectedModuleIds = [],
}) {
  const baseSet = new Set(baseModuleIds || []);
  const selectedSet = new Set(selectedModuleIds || []);
  const moduleIds = new Set([...baseSet, ...selectedSet]);

  return [...moduleIds].reduce((rows, moduleId) => {
    const inherited = baseSet.has(moduleId);
    const selected = selectedSet.has(moduleId);

    if (inherited === selected) return rows;

    rows.push({
      module_id: moduleId,
      is_allowed: selected,
    });

    return rows;
  }, []);
}

// Roles estandar que toda organizacion arranca teniendo. El acceso por rol se
// siembra dando a cada rol los modulos habilitados del plan (igual que el
// patron historico: filas de role_templates = custom_modules x roles). Luego
// el admin afina por rol desde Configuracion -> Roles, o por usuario via
// role_permissions (overrides).
export const DEFAULT_ROLE_NAMES = [
  'admin',
  'administrador',
  'cajero',
  'encargado',
  'operario',
  'vendedor',
];

/**
 * Siembra plantillas de modulos por rol para una organizacion nueva.
 *
 * Por que existe: el modelo de permisos depende de `role_templates`. Si una org
 * nace sin filas, sus usuarios (incluido el admin) ven todos los modulos como
 * "Sin acceso". El onboarding nunca sembraba esta tabla, asi que cada alta
 * quedaba con el dueno bloqueado. Esta funcion deja a cada rol estandar con
 * acceso a los modulos del plan.
 *
 * Es idempotente y conservadora: si la org YA tiene alguna plantilla, no toca
 * nada (no pisa la config manual del admin). Best-effort: el llamador deberia
 * envolverla en try/catch para no abortar el resto del provisioning.
 */
export async function seedDefaultRoleTemplates({
  supabase,
  organizationId,
  moduleIds = [],
  roleNames = DEFAULT_ROLE_NAMES,
}) {
  if (!supabase) {
    throw new Error('Supabase es requerido para sembrar plantillas de rol.');
  }
  if (!organizationId) {
    return { seeded: false, reason: 'missing-org' };
  }

  const modules = [...new Set((moduleIds || []).filter(Boolean))];
  if (modules.length === 0) {
    // Sin lista de modulos del plan no hay nada acotado que sembrar. Una org con
    // custom_modules nulo se trata como "todos los modulos" en runtime, asi que
    // no necesita plantillas para que el admin opere.
    return { seeded: false, reason: 'no-modules' };
  }

  const { count, error: countError } = await supabase
    .from('role_templates')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId);

  if (countError) throw countError;
  if ((count || 0) > 0) {
    return { seeded: false, reason: 'already-configured' };
  }

  const seenRoles = new Set();
  const rows = [];
  for (const roleName of roleNames) {
    const normalizedRole = normalizeRoleName(roleName);
    if (!normalizedRole || seenRoles.has(normalizedRole)) continue;
    seenRoles.add(normalizedRole);
    for (const moduleId of modules) {
      rows.push({
        organization_id: organizationId,
        role_name: normalizedRole,
        module_id: moduleId,
      });
    }
  }

  if (rows.length === 0) {
    return { seeded: false, reason: 'no-rows' };
  }

  const { error: insertError } = await supabase.from('role_templates').insert(rows);
  if (insertError) throw insertError;

  return { seeded: true, count: rows.length };
}

export async function fetchEffectiveModulePermissions({
  supabase,
  organizationId,
  userId,
  roleName,
}) {
  if (!supabase) {
    throw new Error('Supabase es requerido para calcular permisos.');
  }

  if (!organizationId || !userId) {
    return {
      baseRows: [],
      overrideRows: [],
      effective: new Set(),
    };
  }

  const normalizedRole = normalizeRoleName(roleName);

  const [baseResponse, overridesResponse] = await Promise.all([
    supabase
      .from('role_templates')
      .select('module_id')
      .eq('organization_id', organizationId)
      .eq('role_name', normalizedRole),
    supabase
      .from('role_permissions')
      .select('module_id,is_allowed')
      .eq('organization_id', organizationId)
      .eq('user_id', userId),
  ]);

  if (baseResponse.error) throw baseResponse.error;
  if (overridesResponse.error) throw overridesResponse.error;

  const baseRows = baseResponse.data || [];
  const overrideRows = overridesResponse.data || [];

  return {
    baseRows,
    overrideRows,
    effective: buildEffectiveModulePermissions({ baseRows, overrideRows }),
  };
}
